import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Send, Loader2, Star } from "lucide-react";

/**
 * Modal that appears when a user picks "Send to contractor" from the task 3-dot menu.
 * Lets them pick a contractor (with milestone-aware recommendations), then PATCHes
 * the task with both `owner_contractor_id` and `status = "waiting_on_contractor"`.
 */
export default function SendToContractorDialog({
  open,
  onOpenChange,
  task,
  milestoneName,
  firmContractors = [],
  onDone,
}) {
  const [contractorId, setContractorId] = useState(task?.owner_contractor_id || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset selection when task/dialog reopens
  React.useEffect(() => {
    if (open) {
      setContractorId(task?.owner_contractor_id || "");
      setNote("");
    }
  }, [open, task]);

  const { recommended, others } = useMemo(() => {
    const key = (milestoneName || "").toLowerCase();
    const rec = [];
    const oth = [];
    (firmContractors || []).forEach((c) => {
      const specs = (c.specialties || []).map((s) => s.toLowerCase());
      if (key && specs.includes(key)) rec.push(c);
      else oth.push(c);
    });
    const sortFn = (a, b) =>
      (a.is_favorite ? -1 : 0) - (b.is_favorite ? -1 : 0) || a.name.localeCompare(b.name);
    rec.sort(sortFn);
    oth.sort(sortFn);
    return { recommended: rec, others: oth };
  }, [firmContractors, milestoneName]);

  const submit = async () => {
    if (!contractorId) {
      toast.error("Pick a contractor first");
      return;
    }
    setSaving(true);
    try {
      // 1) assign + move to contractor phase
      await api.patch(`/tasks/${task.task_id}`, {
        owner_contractor_id: contractorId,
        status: "waiting_on_contractor",
      });
      // 2) if a note was written, post it as the first contractor update
      if (note.trim()) {
        const fd = new FormData();
        fd.append("note", `Assigned by ID firm: ${note.trim()}`);
        await api.post(`/tasks/${task.task_id}/contractor-updates`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const c = firmContractors.find((x) => x.contractor_id === contractorId);
      toast.success(`Sent to ${c?.name || "contractor"}`);
      onDone && (await onDone());
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const renderOption = (c) => (
    <label
      key={c.contractor_id}
      className={`flex items-start gap-3 px-3 py-2.5 rounded-xl cursor-pointer border transition-colors ${
        contractorId === c.contractor_id
          ? "bg-white border-[#2C2A29] shadow-sm"
          : "bg-white/60 border-transparent hover:bg-white"
      }`}
      data-testid={`send-contractor-option-${c.contractor_id}`}
    >
      <input
        type="radio"
        name="contractor"
        checked={contractorId === c.contractor_id}
        onChange={() => setContractorId(c.contractor_id)}
        className="mt-1 accent-[#2C2A29]"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-sm font-semibold text-[#2C2A29]">
          {c.is_favorite && <Star size={12} className="fill-[#F59E0B] text-[#F59E0B]" />}
          <span className="truncate">{c.name}</span>
          {c.firm_name && <span className="text-[#7A7571] font-normal truncate"> — {c.firm_name}</span>}
        </div>
        {c.specialties?.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {c.specialties.slice(0, 4).map((s) => (
              <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F7F4EC] text-[#7A7571]">
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
    </label>
  );

  const nothingToShow = recommended.length === 0 && others.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[720px] w-[95vw] rounded-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="send-to-contractor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send task to contractor</DialogTitle>
          <DialogDescription>
            Pick who should take this on. Recommended contractors are matched to the task&rsquo;s milestone
            {milestoneName ? ` (${milestoneName})` : ""} via their specialties in your Contractor Directory.
          </DialogDescription>
        </DialogHeader>

        {task && (
          <div className="text-sm text-[#2C2A29] card-soft !bg-[#F7F4EC] !py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-0.5">Task</div>
            <div className="font-semibold truncate">{task.title}</div>
            {task.item_code && <div className="text-xs text-[#7A7571] mt-0.5">Item #{task.item_code}</div>}
          </div>
        )}

        <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-4 py-3">
          {nothingToShow ? (
            <div className="text-sm text-[#7A7571] italic p-6 text-center bg-[#FBF7EF] rounded-2xl">
              No contractors in your Contractor Directory yet. Add one under{" "}
              <span className="font-semibold text-[#2C2A29]">Contractors</span> before sending tasks out.
            </div>
          ) : (
            <>
              {recommended.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">
                    Recommended {milestoneName ? `for ${milestoneName}` : ""}
                  </div>
                  <div className="space-y-1.5">
                    {recommended.map(renderOption)}
                  </div>
                </div>
              )}
              {others.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">
                    {recommended.length > 0 ? "Other contractors" : "Contractors"}
                  </div>
                  <div className="space-y-1.5">
                    {others.map(renderOption)}
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">
              Optional note to the contractor
            </label>
            <textarea
              className="input-soft min-h-[70px] text-sm"
              placeholder="e.g. Site is ready — please reach out to the homeowner directly on WhatsApp."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="send-contractor-note"
            />
            <p className="text-[11px] text-[#7A7571] mt-1">
              If added, this posts as the first entry in the task&rsquo;s contractor update log.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#EBE5D9]">
          <button className="btn-secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={submit}
            disabled={saving || !contractorId}
            data-testid="send-contractor-confirm"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {saving ? "Sending…" : "Send to contractor"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
